import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Leaf } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";

/**
 * FarmersPulseAlmanac — long-form editorial about Farmers Pulse as
 * a Living Almanac. Rendered at /farmers/almanac and linked from the
 * "Read More →" pill on the /farmers home intro block.
 */
export default function FarmersPulseAlmanac() {
  const heroTxt = "Farmers Pulse — The Living Almanac for Farmers Everywhere. A growing global agricultural information, observation, and knowledge platform designed to help farmers understand what is happening around them right now.";

  return (
    <main
      className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] text-[#241209] pt-32 md:pt-36 pb-24"
      data-testid="farmers-almanac"
    >
      <article className="max-w-3xl mx-auto px-5 md:px-8">
        <Link
          to="/farmers"
          data-testid="farmers-almanac-back"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]"
        >
          <ArrowLeft size={11} weight="bold" /> Back to Farmers Pulse
        </Link>

        <header className="mt-6">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
            <BookOpen size={12} weight="fill" /> Farmers Pulse · Editorial
          </div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
            The Living Almanac for Farmers Everywhere
          </h1>
          <p className="mt-4 text-lg text-[#3A3830]">
            From the Traditional Almanac to a Living Almanac — a growing global agricultural information, observation, and knowledge platform designed for farmers who know the land best.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <ListenTranslateBar text={heroTxt} testidPrefix="farmers-almanac-tools" />
            <ShareButton
              title="Farmers Pulse — The Living Almanac for Farmers Everywhere"
              text="A growing global agricultural information, observation, and knowledge platform."
              hashtags={["FarmersPulse", "NaturePulse"]}
              variant="chip"
              testid="farmers-almanac-share"
            />
          </div>
        </header>

        <div className="mt-10 max-w-none space-y-5 text-[#241209] text-lg leading-relaxed [&_h2]:font-serif-display [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:tracking-tight [&_h2]:text-[#1B4332] [&_h2]:mt-12 [&_h2]:mb-3 [&_h3]:font-serif-display [&_h3]:text-2xl [&_h3]:text-[#1B4332] [&_h3]:mt-8 [&_h3]:mb-2 [&_strong]:text-[#1B4332] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-[color:var(--np-brand,#B85F2A)] [&_a]:underline">

          <p>
            For thousands of years, farmers have looked to the sky, the soil, plants, birds, insects, seasons, and generations of accumulated knowledge to decide when to plant, when to irrigate, when to harvest, and when to prepare for changing conditions.
          </p>
          <p>
            Traditional farmers' almanacs helped organize much of this knowledge around seasons, weather, planting cycles, the sun, the moon, and the calendar. At <strong>NaturePulse.net</strong>, we want to take that idea much further.
          </p>
          <p>
            We are introducing <strong>Farmers Pulse</strong> — a growing global agricultural information, observation, and knowledge platform designed to help farmers understand not only what normally happens in their region, but also what is happening around them right now.
          </p>

          <h2>From the Traditional Almanac to a Living Almanac</h2>
          <p>Agriculture is local.</p>
          <p>
            A planting recommendation that works in Oregon may be completely inappropriate in Kerala. Conditions in Kenya may be different from Brazil, Australia, Indonesia, or France. Even two villages only a few kilometers apart can experience different rainfall, soil conditions, water availability, pests, and growing cycles.
          </p>
          <p>Farmers Pulse is therefore being designed around a simple principle:</p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
            Start with the farmer's location.
          </blockquote>
          <p>
            From country and state to district, village, and eventually an individual farm, Farmers Pulse can organize useful agricultural information around the conditions that matter locally.
          </p>
          <p>The goal is to bring together four important questions:</p>
          <ul>
            <li>What usually happens here?</li>
            <li>What is happening here now?</li>
            <li>What is changing?</li>
            <li>What should a farmer consider doing next?</li>
          </ul>

          <h2>Your Daily Farmers Pulse</h2>
          <p>
            Imagine beginning the morning by opening Farmers Pulse and seeing a simple agricultural snapshot for your area:
          </p>
          <ul>
            <li><strong>Today's Weather</strong> — Temperature, rainfall, humidity, wind, sunrise, sunset, severe-weather conditions, and forecasts.</li>
            <li><strong>Rain &amp; Water</strong> — Recent rainfall, rainfall trends, drought conditions, flooding, irrigation considerations, and community water observations.</li>
            <li><strong>What to Plant</strong> — Crops that may be appropriate for the current season and location.</li>
            <li><strong>What to Harvest</strong> — Crops commonly reaching harvest in the area.</li>
            <li><strong>Farm Alerts</strong> — Frost, excessive heat, storms, heavy rainfall, flooding, drought, pests, diseases, and other conditions affecting agriculture.</li>
            <li><strong>What's Happening Around Me</strong> — Observations submitted by farmers and NaturePulse contributors nearby.</li>
          </ul>
          <p>
            Instead of presenting farmers with enormous amounts of raw information, Farmers Pulse will work toward answering a practical question:
          </p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
            What does today's environment mean for my farm?
          </blockquote>

          <h2>A Farming Calendar Built Around Your Location</h2>
          <p>Farmers Pulse will develop localized crop calendars covering the agricultural cycle:</p>
          <p className="font-mono-tech text-sm tracking-wider text-[#3A3830]">
            Prepare → Sow → Germinate → Transplant → Grow → Irrigate → Fertilize → Flower → Pollinate → Fruit → Harvest → Store
          </p>
          <p>Farmers will be able to select crops they grow and follow their seasonal progress.</p>
          <p>
            Over time, Farmers Pulse can compare recommended agricultural calendars with what farmers are actually observing. If mango flowering normally begins during one period but farmers across a region begin reporting flowering several weeks earlier, that change becomes valuable information.
          </p>
          <p>
            The same applies to planting, rainfall, pest emergence, pollinator activity, harvesting, frost, drought, and many other natural events.
          </p>

          <h2>My Farm — Your Personal Agricultural Almanac</h2>
          <p>Farmers will be able to create their own private <strong>My Farm</strong> area.</p>
          <p>
            A farmer may record crops, trees, soil information, water sources, planting dates, rainfall, irrigation, flowering, harvests, pests, diseases, photographs, and other observations.
          </p>
          <p>
            A personal <strong>Farm Diary</strong> can gradually become something extremely valuable: a historical record of the farm itself.
          </p>
          <p>After several years, a farmer might be able to ask:</p>
          <ul>
            <li>When did my mango trees flower during each of the last five years?</li>
            <li>How much rain did we receive before last year's rice planting?</li>
            <li>Which month did this pest first appear?</li>
            <li>Was this year's harvest earlier than usual?</li>
            <li>Is our well level declining?</li>
            <li>Are we seeing fewer pollinators than before?</li>
          </ul>
          <p>The farmer's own observations become the foundation of a personalized almanac.</p>

          <h2>Report What You See</h2>
          <p>One of the most important parts of Farmers Pulse will be remarkably simple:</p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
            Report What You See
          </blockquote>
          <p>
            Farmers and NaturePulse contributors will be encouraged to document everyday agricultural and environmental events. A report might be:
          </p>
          <ul>
            <li>🌱 Seeds germinating</li>
            <li>🌸 Mango trees flowering</li>
            <li>🍅 Tomatoes fruiting</li>
            <li>🌾 Rice being harvested</li>
            <li>🐝 Bees visiting flowers</li>
            <li>🐛 A new pest appearing</li>
            <li>🍂 Unusual leaf damage</li>
            <li>🌧 Heavy rainfall</li>
            <li>💧 A well running low</li>
            <li>☀️ Extreme heat</li>
            <li>❄️ Unexpected frost</li>
            <li>🌊 Farmland flooding</li>
          </ul>
          <p>A photograph, date, approximate location, and a few words may be enough.</p>
          <p>
            Artificial intelligence may help suggest the plant, crop, insect, disease, category, description, and tags, while the contributor remains able to correct the information.
          </p>

          <h2>What's Happening Around Me?</h2>
          <p>Individual observations become even more useful when communities can learn from one another.</p>
          <p>A farmer might eventually see:</p>
          <ul>
            <li>8 km away — Mango flowering reported</li>
            <li>14 km away — Heavy rainfall reported</li>
            <li>22 km away — Possible crop pest reported</li>
            <li>31 km away — Increased bee activity observed</li>
            <li>45 km away — Rice harvesting underway</li>
          </ul>
          <p>
            Farmers Pulse can organize these observations geographically while protecting private farm information and precise locations when necessary.
          </p>
          <p>
            This could create a <strong>grassroots agricultural early-warning network</strong>. If farmers across an area suddenly begin reporting the same insect or plant disease, other farmers nearby may have an opportunity to inspect their crops before the problem becomes widespread.
          </p>

          <h2>Pest and Disease Awareness</h2>
          <p>Crop pests and diseases can move rapidly across agricultural regions.</p>
          <p>
            Farmers Pulse aims to combine farmer observations, photographs, expert knowledge, public agricultural information, and artificial intelligence to help identify possible problems earlier.
          </p>
          <p>A farmer could photograph a damaged leaf and ask:</p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
            "What might be causing this?"
          </blockquote>
          <p>
            AI may suggest possibilities, but uncertain identification should always be clearly identified as uncertain. Farmers Pulse will distinguish between AI suggestions, community observations, expert verification, and authoritative agricultural guidance.
          </p>
          <p>
            As reports accumulate, Farmers Pulse could identify geographic patterns and alert nearby farmers to emerging problems.
          </p>

          <h2>Rain Pulse, Water Pulse and Soil Pulse</h2>
          <p>For many farmers, agriculture ultimately depends on three fundamentals:</p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
            Rain. Water. Soil.
          </blockquote>
          <p>Farmers Pulse will develop specialized tools for documenting all three.</p>
          <h3>Rain Pulse</h3>
          <p>Farmers can record rainfall and compare daily, monthly, seasonal, and historical patterns.</p>
          <h3>Water Pulse</h3>
          <p>
            Farmers may record observations about wells, ponds, streams, reservoirs, irrigation systems, flooding, and drought.
          </p>
          <p>A simple observation such as:</p>
          <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
            "The farm well is approximately 60 centimeters lower than it was at this time last year."
          </blockquote>
          <p>may become very important when similar observations are reported throughout a region.</p>
          <h3>Soil Pulse</h3>
          <p>Farmers can maintain information about soil type, moisture, pH, organic matter, nutrients, drainage, and soil-test results.</p>
          <p>Together, these records can help farmers understand how their land is changing.</p>

          <h2>Pollinators, Birds and Wildlife Matter Too</h2>
          <p>A healthy farm is more than crops.</p>
          <p>
            Bees, butterflies, birds, insects, earthworms, microorganisms, and surrounding vegetation are part of a larger ecosystem.
          </p>
          <p>Farmers Pulse will therefore connect directly with the wider NaturePulse observation network.</p>
          <p>Farmers can document pollinators, birds, beneficial insects, wildlife, invasive species, and changing biodiversity.</p>
          <p>
            A butterfly photographed on a coffee farm, for example, could contribute both to an agricultural pollinator observation and to NaturePulse's broader biodiversity records.
          </p>

          <h2>Preserving the Knowledge of Farmers</h2>
          <p>Some of the world's most valuable agricultural knowledge has never appeared in a textbook.</p>
          <p>It has been passed from farmer to farmer, parent to child, and generation to generation.</p>
          <p>Farmers know local signs.</p>
          <p>
            They observe particular birds before seasonal rains. They know when certain trees flower. They recognize changes in winds, insects, clouds, soil, streams, and animal behavior.
          </p>
          <p>Farmers Pulse wants to help preserve this knowledge.</p>
          <p>
            Traditional farming observations can be recorded with their location, language, crop, community, and contributor.
          </p>
          <p>
            They will be identified as <strong>Traditional</strong> or <strong>Community Knowledge</strong>, allowing them to coexist with scientific research without incorrectly presenting tradition as scientifically established fact.
          </p>

          <h2>Ask Farmers Pulse</h2>
          <p>As Farmers Pulse develops, artificial intelligence can make this growing collection of information easier to use.</p>
          <p>A farmer might ask:</p>
          <ul>
            <li>"Is this a good week to plant tomatoes?"</li>
            <li>"How much rain have we received this month?"</li>
            <li>"What pests are farmers reporting near me?"</li>
            <li>"Why might these leaves be turning yellow?"</li>
            <li>"When did my mango trees flower last year?"</li>
            <li>"Compare this year's rainfall with the last five years."</li>
          </ul>
          <p>
            Rather than providing a generic internet answer, the long-term goal is for Farmers Pulse to consider the farmer's location, crops, season, weather, farm history, nearby observations, agricultural research, and authoritative local guidance.
          </p>

          <h2>Designed for Farmers Around the World</h2>
          <p>Farmers Pulse is intended to be global from the beginning.</p>
          <p>That means designing for:</p>
          <p className="font-mono-tech text-sm tracking-wider text-[#3A3830]">
            Countries → States/Provinces → Districts/Counties → Towns → Villages → Farms
          </p>
          <p>
            It also means recognizing that farmers speak different languages, use different measurements, grow different crops, and farm under vastly different conditions.
          </p>
          <p>
            Farmers Pulse will therefore be developed toward <strong>multilingual, mobile-friendly, low-bandwidth</strong>, and eventually <strong>voice-enabled</strong> access.
          </p>
          <p>A farmer should not need sophisticated technical knowledge to participate.</p>
          <p>
            Taking a photograph and saying <em>"This appeared on my banana plants today"</em> should eventually be enough to begin an observation.
          </p>

          <h2>Farmers Helping Farmers — and Science</h2>
          <p>Farmers Pulse can become more valuable as participation grows.</p>
          <ul>
            <li>One farmer reporting an unusual pest helps document a local event.</li>
            <li>Ten farmers reporting it may reveal a local problem.</li>
            <li>Hundreds of reports across a region may reveal an outbreak.</li>
            <li>Years of observations could reveal something larger still.</li>
          </ul>
          <p>
            Researchers may eventually be able to study anonymized and appropriately shared observations of planting dates, flowering, rainfall, harvests, pollinators, pests, drought, flooding, soil, and water.
          </p>
          <p>Such information could contribute to our understanding of how agriculture and nature are responding to a changing climate.</p>
          <p>
            <strong>Privacy will remain essential.</strong> Farmers should determine what information stays private, what is shared with their community, and what may contribute to broader research.
          </p>

          <h2>The Agricultural Pulse of Our Planet</h2>
          <p>Farmers have always been observers of nature.</p>
          <p>
            Long before satellites, weather apps, sensors, and artificial intelligence, farmers watched the clouds, touched the soil, studied plants, listened to birds, observed insects, measured rainfall, and remembered the seasons.
          </p>
          <p>Modern technology should not replace that knowledge.</p>
          <p>It should help connect it.</p>
          <p>That is the vision behind Farmers Pulse on NaturePulse.net.</p>

          <h3>What usually happens.</h3>
          <p>Historical patterns, crop calendars, traditional knowledge, and agricultural science.</p>

          <h3>What is happening now.</h3>
          <p>Weather, rainfall, crops, pests, diseases, flowering, harvesting, water, soil, pollinators, and farmer observations.</p>

          <h3>What is changing.</h3>
          <p>Growing seasons, rainfall patterns, temperatures, water availability, biodiversity, pest activity, flowering times, and harvest dates.</p>

          <h3>What can we learn together?</h3>
          <p>
            By connecting farmers, communities, researchers, technology, and nature itself, Farmers Pulse can gradually become a living agricultural almanac created by the people who know the land best — the farmers who work it every day.
          </p>
          <p>One observation can help one farmer.</p>
          <p>Thousands of observations can help a community.</p>
          <p>Millions of observations can help us understand the changing agricultural pulse of our planet.</p>
        </div>

        {/* Signature closer */}
        <div className="mt-14 rounded-2xl border border-black/10 bg-white/60 p-6 md:p-8">
          <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
            <Leaf size={16} weight="fill" />
            <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase">Farmers Pulse</span>
          </div>
          <div className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight">
            Observe · Record · Learn · Grow · Share
          </div>
          <p className="mt-3 text-[#3A3830]">
            Coming to NaturePulse.net — connecting farmers, farms, communities, agriculture, and nature around the world.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/farmers/report"
              data-testid="farmers-almanac-cta-report"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              Report what you see <ArrowRight size={10} weight="bold" />
            </Link>
            <Link
              to="/farmers/farms/new"
              data-testid="farmers-almanac-cta-farm"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              Add my farm
            </Link>
            <Link
              to="/farmers"
              data-testid="farmers-almanac-cta-home"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              Open Farmers Pulse
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
